import { ReactNode, createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useLinkAccountCheck } from 'src/shared/queries/useLinkAccountCheck';

type LinkAccountContextValue = {
  needsLinking: boolean;
  primaryUserId: string | null;
  isChecking: boolean;
};

const LinkAccountContext = createContext<LinkAccountContextValue>({
  needsLinking: false,
  primaryUserId: null,
  isChecking: false,
});

type LinkAccountProviderProps = {
  children: ReactNode;
  redirectPrefix?: string;
};

export function LinkAccountProvider({ children, redirectPrefix }: LinkAccountProviderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: { needsLinking = false, primaryUserId = null } = {}, isLoading: isChecking } = useLinkAccountCheck();

  useEffect(() => {
    if (!needsLinking) {
      return;
    }

    if (pathname?.includes('/link-account')) {
      return;
    }

    navigate({ to: `${redirectPrefix ?? ''}/link-account` });
  }, [needsLinking, pathname, redirectPrefix, navigate]);

  const value = useMemo(
    () => ({
      needsLinking,
      primaryUserId,
      isChecking,
    }),
    [needsLinking, primaryUserId, isChecking]
  );

  return <LinkAccountContext.Provider value={value}>{children}</LinkAccountContext.Provider>;
}

export function useLinkAccountContext() {
  return useContext(LinkAccountContext);
}
